<template>
  <div class="bg-gray-50 min-h-full">
    <Sidebar :content="sidebarContent">
      <div v-if="cameraNavbarTabs.includes(activeTag)">
        <!-- Don't show camera navbar for analytics features -->
        <PageHeader v-if="currentStream">{{ this.currentStream.name }}</PageHeader>
        <div class="md:px-6 px-4">
          <CameraNavbar
            :initial-active-tag="activeTag"
            :stream="this.currentStream"
            v-if="currentStream"
          />
          <slot></slot>
        </div>
      </div>
      <div v-else>
        <slot></slot>
      </div>
    </Sidebar>
  </div>
</template>

<script>
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  HomeIcon,
  CameraIcon,
  Bars3CenterLeftIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
  Cog8ToothIcon,
  ChartBarIcon,
} from "@heroicons/vue/24/solid";
import { defineComponent } from "vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import Sidebar from "@/components/layout/Sidebar.vue";
import { useHasProjectFeature } from "@/composables/project";
import { useStreams } from "@/composables/stream";
import { getConfig } from "@/services/config";
import CameraNavbar from "@/views/camera/components/CameraNavbar.vue";

export default defineComponent({
  name: "Navigation Component",
  props: {
    activeTag: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      currentStream: null,
      sidebarContent: [],
      analyticsTabs: [],
      settingsTabs: [],
      cameraOptions: [
        "Live",
        "Period",
        "Compare",
        "Gallery",
        "DailyTimelapse",
        "ProjectTimelapse",
        "CustomizableTimelapse",
      ],
    };
  },
  components: {
    PageHeader,
    Sidebar,
    CameraNavbar,
  },
  setup() {
    const hasWorkingHoursFeature = useHasProjectFeature("working_hours");
    const { streams, isLoading } = useStreams();
    return { streams, isLoading, hasWorkingHoursFeature };
  },
  watch: {
    isLoading() {
      this.addAllContent();
    },
  },
  mounted() {
    this.addAllContent();
    this.setupIntercom();
  },
  computed: {
    cameraNavbarTabs() {
      return [
        this.$t("app_features.live"),
        this.$t("app_features.period"),
        this.$t("app_features.gallery"),
        this.$t("app_features.project_timelapse"),
        this.$t("app_features.daily_timelapse"),
        this.$t("app_features.customizable_timelapse"),
        this.$t("app_features.compare"),
      ];
    },
  },
  methods: {
    addAllContent() {
      if (this.isLoading) {
        return;
      }
      this.filterSidebarContent();
      this.addSidebarContent();
    },
    filterSidebarContent() {
      const analyticsTabs = [];

      if (this.hasPermission("daily_report_user")) {
        analyticsTabs.push({
          name: this.$t("app_features.daily_report"),
          href: "/daily_report/overview",
          icon: DocumentChartBarIcon,
          current: false,
          section: "analytics",
        });
      }
      if (this.hasPermission("planner_user")) {
        analyticsTabs.push({
          name: this.$t("app_features.planner"),
          href: `/planner`,
          icon: CalendarDaysIcon,
          current: false,
          section: "analytics",
        });
      }
      if (this.hasPermission("processes_user")) {
        analyticsTabs.push({
          name: this.$t("app_features.processes"),
          icon: Bars3CenterLeftIcon,
          current: false,
          section: "analytics",
          options: [
            {
              name: this.$t("analytics.processes.process_data_gantt"),
              href: "/processes",
              current: false,
            },
            {
              name: this.$t("analytics.processes.process_data_table"),
              href: "/processes/table",
              current: false,
            },
            this.hasPermission("processes_user") &&
              this.hasWorkingHoursFeature && {
                name: this.$t("analytics.processes.unit_values"),
                href: "/unit_values_editor",
                current: false,
              },
          ].filter((item) => item),
        });
      }
      if (this.hasPermission("processes_user")) {
        analyticsTabs.push({
          name: this.$t("analytics.reports.reports"),
          href: "/reports",
          current: false,
          section: "analytics",
          icon: ChartBarIcon,
        });
      }
      if (this.hasPermission("site_activity_user")) {
        analyticsTabs.push({
          name: this.$t("app_features.site_activity"),
          href: "/labour/site_activity",
          icon: UserGroupIcon,
          current: false,
          section: "analytics",
        });
      }
      if (this.hasPermission("project_admin")) {
        this.settingsTabs = [
          {
            name: this.$t("app_features.project_settings"),
            href: "/settings",
            icon: Cog8ToothIcon,
            current: false,
            section: "project_settings",
          },
        ];
      }

      this.analyticsTabs = analyticsTabs;
    },

    addSidebarContent() {
      if (!this.currentProjectV2) {
        return;
      }
      const customerName = this.currentProjectV2.customer_name;
      const siteId = this.currentProjectV2.site_id;
      const projectStatus = this.currentProjectV2.status;

      let listEntry = {
        name: "Home",
        href: "/",
        icon: BuildingOfficeIcon,
        current: false,
        section: "overview",
      };
      this.sidebarContent.push(listEntry);

      listEntry = {
        name: this.$t("app_features.project_overview"),
        href: `/${customerName}/${siteId}/dashboard`,
        icon: HomeIcon,
        current: this.activeTag === this.$t("app_features.project_overview"),
        section: "overview",
      };
      this.sidebarContent.push(listEntry);

      if (this.hasPermission("live_camera_feature_user")) {
        for (const stream of this.streams) {
          let cameraRoute = "";
          if (this.cameraOptions.includes(this.$route.name)) {
            if (
              (projectStatus === "completed" || !stream.aws_stream_id) &&
              ["Live", "Period"].includes(this.$route?.name)
            ) {
              cameraRoute = "/project_timelapse";
            } else {
              const lastSegment = this.$route?.fullPath.split("/").pop();
              cameraRoute = `/${lastSegment}`;
            }
          } else {
            cameraRoute =
              projectStatus === "completed" || !stream.aws_stream_id
                ? "/project_timelapse"
                : "/live";
          }
          if (
            stream.camera_id === this.$route.params.camera_id &&
            this.activeTag !== this.$t("app_features.site_activity")
          ) {
            this.currentStream = stream;

            // Add active sidebar tag
            const listEntry = {
              name: stream.name,
              href: `/${customerName}/${siteId}/${stream.camera_id}${cameraRoute}`,
              icon: CameraIcon,
              current: true,
              section: "camera",
            };
            this.sidebarContent.push(listEntry);
          } else {
            // Add inactive sidebar tag
            const listEntry = {
              name: stream.name,
              href: `/${customerName}/${siteId}/${stream.camera_id}${cameraRoute}`,
              icon: CameraIcon,
              current: false,
              section: "camera",
            };
            this.sidebarContent.push(listEntry);
          }
        }
      }
      // Add analytics content
      for (const listEntry of [...this.analyticsTabs, ...this.settingsTabs]) {
        if (listEntry.options?.length) {
          listEntry.options.forEach((option) => {
            option["href"] = `/${customerName}/${siteId}${option["href"]}`;
          });
        } else {
          listEntry["href"] = `/${customerName}/${siteId}${listEntry["href"]}`;
        }

        // Check if item is active Tag
        if (listEntry["name"] === this.activeTag) {
          listEntry["current"] = true;
        }
        this.sidebarContent.push(listEntry);
      }
    },
    setupIntercom() {
      const config = getConfig();

      if (process.env.NODE_ENV !== "production") {
        return;
      }

      const user = this.$store.state.user;
      window.intercomSettings = {
        api_base: config.INTERCOM_API_BASE,
        app_id: config.INTERCOM_APP_ID,
        name: user.name, // Full name
        email: user.email,
        created_at: Date.now().toString(), // Signup date as a Unix timestamp
      };

      (function (...rest) {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === "function") {
          ic("reattach_activator");
          ic("update", w.intercomSettings);
        } else {
          const d = document;
          const i = function () {
            i.c(rest);
          };
          i.q = [];
          i.c = function (args) {
            i.q.push(args);
          };
          w.Intercom = i;
          const l = function () {
            const s = d.createElement("script");
            s.type = "text/javascript";
            s.async = true;
            s.src = `${config.INTERCOM_WIDGET_API_BASE}/${config.INTERCOM_APP_ID}`;
            const x = d.getElementsByTagName("script")[0];
            x.parentNode.insertBefore(s, x);
          };
          if (document.readyState === "complete") {
            l();
          } else if (w.attachEvent) {
            w.attachEvent("onload", l);
          } else {
            w.addEventListener("load", l, false);
          }
        }
      })();
    },
  },
});
</script>
